import React from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const LocationContactSection = ({ formData, setFormData, errors }) => {
  const updateLocation = (field, value) => {
    setFormData(prev => ({
      ...prev,
      location: { ...prev?.location, [field]: value }
    }));
  };

  const updateContact = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contact: { ...prev?.contact, [field]: value }
    }));
  };

  const updateSocialMedia = (field, value) => {
    setFormData(prev => ({
      ...prev,
      socialMedia: { ...prev?.socialMedia, [field]: value }
    }));
  };

  const updateFeatures = (field, value) => {
    setFormData(prev => ({
      ...prev,
      features: { ...prev?.features, [field]: value }
    }));
  };

  const updateOpeningHours = (day, value) => {
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev?.location,
        openingHours: {
          ...prev?.location?.openingHours,
          [day]: value
        }
      }
    }));
  };

  // Fixed: Use proper case for weekday values to prevent Date.toLocaleDateString error
  const daysOfWeek = [
    { key: 'monday', label: 'Monday', weekday: 'Monday' },
    { key: 'tuesday', label: 'Tuesday', weekday: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday', weekday: 'Wednesday' },
    { key: 'thursday', label: 'Thursday', weekday: 'Thursday' },
    { key: 'friday', label: 'Friday', weekday: 'Friday' },
    { key: 'saturday', label: 'Saturday', weekday: 'Saturday' },
    { key: 'sunday', label: 'Sunday', weekday: 'Sunday' }
  ];

  return (
    <div className="space-y-6">
      {/* Location Information */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="MapPin" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Location Information
            </h2>
            <p className="text-muted-foreground text-sm">
              Where can clients find your business?
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Address */}
          <div className="md:col-span-2">
            <Input
              label="Full Address"
              placeholder="123 Wellness Street"
              value={formData?.location?.address || ''}
              onChange={(e) => updateLocation('address', e?.target?.value)}
              error={errors?.['location.address']}
              required
            />
          </div>

          {/* City */}
          <div>
            <Input
              label="City"
              placeholder="London"
              value={formData?.location?.city || ''}
              onChange={(e) => updateLocation('city', e?.target?.value)}
              error={errors?.['location.city']}
              required
            />
          </div>

          {/* Postcode */}
          <div>
            <Input
              label="Postcode"
              placeholder="SW1A 1AA"
              value={formData?.location?.postcode || ''}
              onChange={(e) => updateLocation('postcode', e?.target?.value?.toUpperCase())}
              error={errors?.['location.postcode']}
              required
            />
          </div>

          {/* Show Map Toggle */}
          <div className="md:col-span-2">
            <Checkbox
              label="Show map on business detail page"
              checked={formData?.location?.showMap || false}
              onChange={(checked) => updateLocation('showMap', checked)}
            />
          </div>
        </div>
      </div>
      
      {/* Opening Hours */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Clock" size={16} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">Opening Hours</h3>
            <p className="text-muted-foreground text-sm">
              When are you available for clients?
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {daysOfWeek?.map(({ key, label }) => (
            <div key={key} className="flex items-center space-x-3">
              <label className="w-20 text-sm font-medium text-foreground">
                {label}
              </label>
              <Input
                placeholder="e.g., 9:00 - 17:00 or Closed"
                value={formData?.location?.openingHours?.[key] || ''}
                onChange={(e) => updateOpeningHours(key, e?.target?.value)}
                className="flex-1"
              />
            </div>
          ))}
        </div>
      </div>
      
      {/* Contact Information */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Phone" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Contact Information
            </h2>
            <p className="text-muted-foreground text-sm">
              How can clients get in touch with you?
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Phone */}
          <div>
            <Input
              label="Phone Number"
              type="tel"
              placeholder="+44 20 1234 5678"
              value={formData?.contact?.phone || ''}
              onChange={(e) => updateContact('phone', e?.target?.value)}
              error={errors?.['contact.phone']}
              required
            />
          </div>

          {/* Email */}
          <div>
            <Input
              label="Email Address"
              type="email"
              placeholder="hello@serenitycentre.co.uk"
              value={formData?.contact?.email || ''}
              onChange={(e) => updateContact('email', e?.target?.value)}
              error={errors?.['contact.email']}
              required
            />
          </div>

          {/* Website */}
          <div className="md:col-span-2">
            <Input
              label="Website"
              type="url"
              placeholder="https://www.serenitycentre.co.uk"
              value={formData?.contact?.website || ''}
              onChange={(e) => updateContact('website', e?.target?.value)}
            />
          </div>
        </div>
      </div>
      
      {/* Social Media */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Share2" size={16} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">Social Media</h3>
            <p className="text-muted-foreground text-sm">
              Connect with your community online
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Facebook"
            placeholder="https://facebook.com/yourpage"
            value={formData?.socialMedia?.facebook || ''}
            onChange={(e) => updateSocialMedia('facebook', e?.target?.value)}
          />
          <Input
            label="Instagram"
            placeholder="https://instagram.com/youraccount"
            value={formData?.socialMedia?.instagram || ''}
            onChange={(e) => updateSocialMedia('instagram', e?.target?.value)}
          />
          <Input
            label="X (formerly Twitter)"
            placeholder="https://x.com/youraccount"
            value={formData?.socialMedia?.x || ''}
            onChange={(e) => updateSocialMedia('x', e?.target?.value)}
          />
          <Input
            label="LinkedIn"
            placeholder="https://linkedin.com/company/yourcompany"
            value={formData?.socialMedia?.linkedin || ''}
            onChange={(e) => updateSocialMedia('linkedin', e?.target?.value)}
          />
        </div>
      </div>
      
      {/* Additional Features */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="CheckCircle" size={16} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">Additional Features</h3>
            <p className="text-muted-foreground text-sm">
              What amenities and services do you offer?
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Checkbox
            label="Parking Available"
            checked={formData?.features?.parkingAvailable || false}
            onChange={(checked) => updateFeatures('parkingAvailable', checked)}
          />
          <Checkbox
            label="Wheelchair Accessible"
            checked={formData?.features?.wheelchairAccessible || false}
            onChange={(checked) => updateFeatures('wheelchairAccessible', checked)}
          />
          <Checkbox
            label="Public Transport Nearby"
            checked={formData?.features?.publicTransportNearby || false}
            onChange={(checked) => updateFeatures('publicTransportNearby', checked)}
          />
          <Checkbox
            label="Online Booking Available"
            checked={formData?.features?.onlineBooking || false}
            onChange={(checked) => updateFeatures('onlineBooking', checked)}
          />
        </div>
      </div>
    </div>
  );
};

export default LocationContactSection;